<!-- src/lib/components/AgentDrawer.svelte -->
<script lang="ts">
  import { agentStore } from '../stores/agents.svelte.ts';
  import { api } from '../api.ts';
  import { fly, fade } from 'svelte/transition';
  import { tick } from 'svelte';

  interface AgentCost {
    agent_id: string;
    input_tokens: number;
    output_tokens: number;
    estimated_usd: number;
    model: string | null;
  }

  interface Props {
    agentId: string | null;
    onClose: () => void;
  }
  let { agentId, onClose }: Props = $props();

  let agentCost = $state<AgentCost | null>(null);

  let outputEl:  HTMLPreElement | null = $state(null);
  let canvasEl:  HTMLCanvasElement | null = $state(null);
  let wrapEl:    HTMLDivElement | null = $state(null);

  // EEG data: 120 samples at 100ms each = 12s history
  const SAMPLES = 120;
  let samples: number[] = Array(SAMPLES).fill(0);
  let lastOutputLen = 0;
  let rafId = 0;

  function drawEeg() {
    if (!canvasEl || !wrapEl) { rafId = requestAnimationFrame(drawEeg); return; }

    const w = wrapEl.clientWidth;
    const h = wrapEl.clientHeight;
    if (canvasEl.width !== w)  canvasEl.width  = w;
    if (canvasEl.height !== h) canvasEl.height = h;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) { rafId = requestAnimationFrame(drawEeg); return; }

    ctx.clearRect(0, 0, w, h);

    const maxSample = Math.max(...samples, 1);
    const isActive  = samples.slice(-5).some(s => s > 0);
    const baseline  = h * 0.72;

    // Faint baseline
    ctx.beginPath();
    ctx.moveTo(0, baseline);
    ctx.lineTo(w, baseline);
    ctx.strokeStyle = 'oklch(0.72 0.28 310 / 0.05)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Waveform via quadratic curves for organic smoothing
    ctx.beginPath();
    ctx.strokeStyle = isActive ? 'oklch(0.72 0.28 310 / 0.26)' : 'oklch(0.72 0.28 310 / 0.12)';
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';

    const step = w / (SAMPLES - 1);
    const pointY = (i: number) => baseline - (samples[i] / maxSample) * h * 0.55;

    ctx.moveTo(0, pointY(0));
    for (let i = 1; i < SAMPLES - 1; i++) {
      const mx = (i * step + (i + 1) * step) / 2;
      const my = (pointY(i) + pointY(i + 1)) / 2;
      ctx.quadraticCurveTo(i * step, pointY(i), mx, my);
    }
    ctx.lineTo((SAMPLES - 1) * step, pointY(SAMPLES - 1));
    ctx.stroke();

    rafId = requestAnimationFrame(drawEeg);
  }

  $effect(() => {
    if (!agentId) return;

    // Reset on new agent
    samples = Array(SAMPLES).fill(0);
    lastOutputLen = 0;

    // Sample output rate every 100ms
    const iv = setInterval(() => {
      if (!agentId) return;
      const len = (agentStore.outputs[agentId] ?? []).join('').length;
      const delta = Math.max(0, len - lastOutputLen);
      lastOutputLen = len;
      samples = [...samples.slice(-(SAMPLES - 1)), delta];
    }, 100);

    // Start render loop
    rafId = requestAnimationFrame(drawEeg);

    return () => {
      clearInterval(iv);
      cancelAnimationFrame(rafId);
    };
  });

  $effect(() => {
    if (!agentId) { agentCost = null; return; }
    const id = agentId;
    api<AgentCost>(`/api/v1/agents/${id}/cost`)
      .then(c => { agentCost = c; })
      .catch(() => { agentCost = null; });
  });

  $effect(() => {
    if (!agentId) return;
    // Track outputs length to re-trigger on new output
    void agentStore.outputs[agentId]?.length;
    let cancelled = false;
    tick().then(() => {
      if (!cancelled && outputEl) outputEl.scrollTop = outputEl.scrollHeight;
    });
    return () => { cancelled = true; };
  });

  function stripAnsi(raw: string): string {
    // Strip ANSI/VT100 escape sequences (colors, cursor movement, etc.)
    // eslint-disable-next-line no-control-regex
    return raw.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '').replace(/\x1b\][^\x07]*\x07/g, '');
  }

  function outputText(): string {
    if (!agentId) return '';
    return stripAnsi((agentStore.outputs[agentId] ?? []).join(''));
  }

  function agentLabel(): string {
    if (!agentId) return '';
    const running = agentStore.running.find(r => r.agent_id === agentId);
    const queued = agentStore.queued.find(q => q.agent_id === agentId);
    const prompt = running?.prompt || queued?.prompt || '';
    return prompt.length > 48 ? prompt.slice(0, 48) + '…' : prompt || agentId.slice(0, 12);
  }

  const tier = $derived(
    agentId ? (agentStore.running.find(r => r.agent_id === agentId)?.tier ?? null) : null
  );

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if agentId}
  <!-- Overlay -->
  <button
    type="button"
    class="overlay"
    transition:fade={{ duration: 150 }}
    onclick={onClose}
    aria-label="Close drawer"
  ></button>

  <!-- Drawer -->
  <aside class="drawer" transition:fly={{ x: 480, duration: 250, opacity: 1 }}>
    <header>
      <div class="title">
        <span class="dot running"></span>
        <span class="label">{agentLabel()}</span>
        {#if tier}
          <span class="tier-badge tier-{tier}">{tier}</span>
        {/if}
      </div>
      <button onclick={onClose} class="close-btn" aria-label="Close">✕ Close</button>
    </header>

    {#if agentCost && (agentCost.input_tokens > 0 || agentCost.output_tokens > 0)}
      <div class="cost-indicator">
        <span class="cost-label">Est. cost</span>
        <span class="cost-value">${agentCost.estimated_usd.toFixed(4)}</span>
        <span class="cost-sep">·</span>
        <span class="cost-tokens">{(agentCost.input_tokens + agentCost.output_tokens).toLocaleString()} tokens</span>
        {#if agentCost.model}
          <span class="cost-sep">·</span>
          <span class="cost-model">{agentCost.model}</span>
        {/if}
      </div>
    {/if}

    <div class="output-wrap" bind:this={wrapEl}>
      <canvas bind:this={canvasEl} class="eeg-canvas"></canvas>
      <pre bind:this={outputEl} class="output">{outputText() || '(awaiting output…)'}</pre>
    </div>
  </aside>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 40;
    cursor: pointer;
    border: none;
    padding: 0;
  }
  .drawer {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: 50vw;
    min-width: 440px;
    max-width: 900px;
    background: #080a0e;
    border-left: 1px solid oklch(0.74 0.28 310 / 0.19);
    z-index: 50;
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 40px rgba(0, 0, 0, 0.7), -2px 0 12px oklch(0.74 0.28 310 / 0.09);
  }
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid oklch(0.74 0.28 310 / 0.12);
    flex-shrink: 0;
    background: oklch(0.74 0.28 310 / 0.06);
  }
  .title {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dot.running {
    background: oklch(0.80 0.26 310);
    box-shadow: 0 0 8px oklch(0.74 0.28 310 / 0.84);
    animation: green-pulse 1.5s ease-in-out infinite;
  }
  @keyframes green-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 8px oklch(0.74 0.28 310 / 0.84); }
    50% { opacity: 0.5; box-shadow: 0 0 3px oklch(0.74 0.28 310 / 0.34); }
  }
  .label {
    color: #d0d4e0;
    font-size: 0.875rem;
    font-family: system-ui, sans-serif;
  }
  .close-btn {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #666;
    padding: 0.3rem 0.875rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .close-btn:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #999;
  }
  .cost-indicator {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.4rem 1.25rem;
    background: rgba(0,0,0,0.2);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    flex-shrink: 0;
  }
  .cost-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.2);
    font-family: 'JetBrains Mono', monospace;
  }
  .cost-value {
    font-size: 0.72rem;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.78 0.26 310 / 0.58);
    font-weight: 600;
  }
  .cost-sep {
    color: rgba(255,255,255,0.1);
    font-size: 0.65rem;
  }
  .cost-tokens {
    font-size: 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    color: rgba(255,255,255,0.2);
  }
  .cost-model {
    font-size: 0.62rem;
    font-family: 'JetBrains Mono', monospace;
    color: rgba(255,255,255,0.15);
  }

  .output-wrap {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .eeg-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0.9;
  }
  .tier-badge {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 7px;
    border-radius: 4px;
    opacity: 0.9;
    flex-shrink: 0;
  }
  .tier-badge.tier-claude-cli {
    background: oklch(0.74 0.28 310 / 0.14);
    color: oklch(0.80 0.26 310);
    border: 1px solid oklch(0.74 0.28 310 / 0.26);
  }
  .tier-badge.tier-openrouter {
    background: oklch(0.55 0.20 265 / 0.10);
    color: oklch(0.58 0.22 265);
    border: 1px solid oklch(0.55 0.20 265 / 0.22);
  }
  .tier-badge.tier-ollama {
    background: oklch(0.78 0.16 85 / 0.10);
    color: oklch(0.78 0.16 85);
    border: 1px solid oklch(0.78 0.16 85 / 0.22);
  }
  pre.output {
    flex: 1;
    overflow-y: auto;
    padding: 1rem 1.25rem;
    font-size: 0.75rem;
    line-height: 1.6;
    color: #00cc6a;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;
    scrollbar-width: thin;
    scrollbar-color: oklch(0.74 0.28 310 / 0.24) transparent;
  }
</style>
